<script lang="ts" setup>
import { onMounted, ref } from 'vue'
import { DatePicker } from '@ark-ui/vue/date-picker'
import { useFetch } from '@/home/hooks/fetch';
import { parseISO, format, isSameYear, isSameMonth, isSameDay } from 'date-fns';

const { $fetch } = useFetch();
const today = new Date()
const calendarList = ref<Record<string, Record<string, any>[]>>({})
const selectedDayEvents = ref<Record<string, any>[]>([])
const selectedDay = ref<Record<string, number>>({
  year: today.getFullYear(),
  month: today.getMonth() + 1,
  day: today.getDate(),
})

const isSelected = (day: DatePicker.DateValue) => {
  return (
    selectedDay.value &&
    selectedDay.value.year === day.year &&
    selectedDay.value.month === day.month &&
    selectedDay.value.day === day.day
  )
}

const handleSelectDay = (day: DatePicker.DateValue) => {
  selectedDay.value = { year: day.year, month: day.month, day: day.day }
  selectedDayEvents.value = calendarList.value[String(day.day)] ? [...calendarList.value[String(day.day)]] : []
}

const getEventCount = (day: number) => {
  const count = calendarList.value[String(day)]?.length || 0
  return count > 3 ? 3 : count
}

const getCalendarList = async (year: number, month: number) => {
  const { isSuccess, data } = await $fetch('/activity/list/currentMonth', {
    method: "POST",
    data: {
      yearMonth: `${year}-${String(month).padStart(2, '0')}`,
    },
  })
  if (isSuccess) {
    calendarList.value = data || {}
  }
}

const formatDateRange = (startDate: string, endDate: string) => {
  if (!startDate || !endDate) { return "长期" }
  const start = parseISO(startDate);
  const end = parseISO(endDate);

  if (!isSameYear(start, end) || !isSameMonth(start, end)) {
    return `${format(start, 'M月d日')} - ${format(end, 'M月d日')}`;
  }
  else if (!isSameDay(start, end)) {
    return `${format(start, 'M月d日')} - ${format(end, 'd日')}`;
  }
  else if (format(start, 'HH:mm:ss') === '00:00:00' && format(end, 'HH:mm:ss') === '23:59:59') {
    return `${format(start, 'M月d日')}`;
  }

  return `${format(start, 'M月d日 HH:mm')} - ${format(end, 'HH:mm')}`;
};

const formatDateString = (date: Date) => {
  const year = date.getFullYear()
  const month = date.getMonth() + 1
  const day = date.getDate()
  const weekDayMap = ['日', '一', '二', '三', '四', '五', '六']
  const weekDay = weekDayMap[date.getDay()]

  return `${year}年${month}月${day}日 星期${weekDay}`
}

const getDayCellClass = (day: DatePicker.DateValue) => {
  if (isSelected(day)) {
    return 'bg-#1281FF text-#FFFFFF';
  }

  const weekDay = new Date(day.year, day.month - 1, day.day).getDay()

  if (weekDay === 0 || weekDay ===  6) {
    return 'bg-#FFFFFF text-#212121/40';
  }

  return 'bg-#F8F9FA text-#212121'
}

const weekdayMap: Record<string, string> = { Sun: '日', Mon: '一', Tue: '二', Wed: '三', Thu: '四', Fri: '五', Sat: '六' }

const calendarCardClass = [
  { text: 'text-#B54708', background: 'bg-#FEF0C7', line: 'bg-#F79009', point: 'bg-#F79009' },
  { text: 'text-#067647', background: 'bg-#DCFAE6', line: 'bg-#17B26A', point: 'bg-#17B26A' },
  { text: 'text-#6941C6', background: 'bg-#F4EBFF', line: 'bg-#9E77ED', point: 'bg-#9E77ED' },
]

onMounted(async () => {
  await getCalendarList(selectedDay.value.year, selectedDay.value.month);

  selectedDayEvents.value = calendarList.value[String(selectedDay.value.day)] ? [...calendarList.value[String(selectedDay.value.day)]] : []
});
</script>

<template>
  <div class="mt-80">
    <div class="flex rounded-38 bg-#fff">
      <div class="w-562 border-r-1 border-#E7E7E7 border-r-solid pb-70 pl-90 pr-64 pt-56">
        <h2 class="m-0 text-32 text-#212121 font-600 leading-45">活动日历</h2>

        <DatePicker.Root open :close-on-select="false" class="mt-16">
          <DatePicker.Positioner class="static! transform-none!">
            <DatePicker.Content>
              <DatePicker.Context v-slot="api">
                <DatePicker.View view="day">
                  <DatePicker.ViewControl class="flex-y-center justify-between">
                    <div class="text-16 text-#212121 font-600 leading-20">{{ `${api.focusedValue.year}年 ${api.focusedValue.month}月` }}</div>
                    <div class="flex gap-20">
                      <DatePicker.PrevTrigger as-child @click="getCalendarList(api.focusedValue.year, api.focusedValue.month - 1)">
                        <div class="i-icons:arrow-left size-20 cursor-pointer text-#6941C6"></div>
                      </DatePicker.PrevTrigger>
                      <DatePicker.NextTrigger as-child @click="getCalendarList(api.focusedValue.year, api.focusedValue.month + 1)">
                        <div class="i-icons:arrow-right size-20 cursor-pointer text-#6941C6 font-bold"></div>
                      </DatePicker.NextTrigger>
                    </div>
                  </DatePicker.ViewControl>

                  <DatePicker.Table class="mt-24 w-408 border-collapse table-fixed">
                    <DatePicker.TableHead class="h-46">
                      <DatePicker.TableRow>
                        <th v-for="(weekDay, id) in api.weekDays" :key="id" class="w-1/7 text-center text-16 text-#212121 font-400 leading-22">
                          {{ weekdayMap[weekDay.short] }}
                        </th>
                      </DatePicker.TableRow>
                    </DatePicker.TableHead>
                    <DatePicker.TableBody>
                      <DatePicker.TableRow v-for="(week, id) in api.weeks" :key="id" class="text-16 text-#212121 font-400 leading-22">
                        <DatePicker.TableCell v-for="(day, idx) in week" :key="idx" :value="day" :class="['pt-6 pb-6', idx === 0 ? 'pl-0 pr-6' : idx === 6 ? 'pl-6 pr-0' : 'px-6']">
                          <DatePicker.TableCellTrigger as-child v-if="day.month === api.focusedValue.month" @click="handleSelectDay(day)">
                            <div class="relative h-48 w-48 flex cursor-pointer items-center justify-center rounded-24 text-center text-16 font-700" :class="getDayCellClass(day)">
                              <div>{{ day.day }}</div>
                              <div v-if="getEventCount(day.day) > 0" class="absolute top-38 flex items-center justify-center gap-3 rounded-full text-10 font-600">
                                <div v-for="(i, index) in getEventCount(day.day)" :key="i" class="size-3 rounded-50%" :class="calendarCardClass[index].point"></div>
                              </div>
                            </div>
                          </DatePicker.TableCellTrigger>
                        </DatePicker.TableCell>
                      </DatePicker.TableRow>
                    </DatePicker.TableBody>
                  </DatePicker.Table>
                </DatePicker.View>
              </DatePicker.Context>
            </DatePicker.Content>
          </DatePicker.Positioner>
        </DatePicker.Root>

        <div class="mt-42 text-14 text-#212121 font-400">中国-北京</div>
      </div>

      <div class="w-full pl-48 pr-64 pt-56">
        <h2 class="m-0 text-26 text-#212121 font-600 leading-36">日程</h2>
        <div class="mt-18 text-16 text-#212121 font-600 leading-20">  {{ formatDateString(new Date(selectedDay.year, selectedDay.month - 1, selectedDay.day)) }}</div>
        <div class="mt-24 h-408 overflow-y-auto">
          <div class="flex flex-col gap-24">
            <div v-for="(event, index) in selectedDayEvents" :key="index" :class="[calendarCardClass[index % 3].text, calendarCardClass[index % 3].background]" class="h-120 w-full flex gap-12 overflow-hidden rounded-l-4 pr-24">
              <div class="w-3" :class="calendarCardClass[index % 3].line"></div>
              <div class="flex-1">
                <div class="mt-8 text-10 font-400 leading-12">活动{{ ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十'][index] }}</div>
                <h3 class="line-clamp-1 m-0 mt-6 text-12 font-600 leading-18">{{ event.atName }}</h3>
                <!-- eslint-disable-next-line vue/no-v-html -->
                <div class="marked line-clamp-2 m-0 mt-4 h-40 text-12 font-400 leading-20">{{ event.atDesc }}</div>
                <div class="m-0 mt-4 flex-y-center gap-4 text-12 font-500 leading-18">
                  <div class="i-icons:schedule size-16 shrink-0"></div>
                  <div class="line-clamp-1">{{ formatDateRange(event.atStartTime, event.atEndTime) }} {{ event.location ?? '' }}</div>
                  <a v-if="event.jumpUrl" :href="event.jumpUrl" target="_blank" class="ml-4 shrink-0 cursor-pointer text-#1281FF font-400 no-underline">了解详情</a>
                </div>
              </div>
            </div>
            <div v-if="selectedDayEvents.length === 0" class="mx-auto mt-125">
              <img src="@/home/assets/home/4/calendar.png" class="h-95 w-137 object-cover">
              <p class="mt-16 text-14 text-black font-400 leading-20">当日没有活动，敬请期待</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.marked::v-deep() {
  p {
    margin: 0;
  }
}
</style>
